import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import React, { useMemo, useState } from 'react'
import { LoadingIcon, Tooltip, theme } from '@clickcease/cc-client-components-lib'
import { FlexRow } from 'Providers/app.styled'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { kFormatter } from 'Utils/number'
import {
  CurrentPlanWrapper,
  CurrentPlanTitle,
  CurrentPlanDetailsWrapper,
  PlanList,
  PlanListItem,
  PlanNameItem,
  PlanClicksItem,
  PlanPriceBold,
  CurrentPlanDetailsRow,
  CurrentPlanPrice,
  LoaderContainer,
  SwitchPaymentInterval,
  InlineFlex,
} from './CurrentPlanDetails.styled'
import { useTranslation } from 'react-i18next'

import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'
import { getProductName } from 'Utils/productName'
import { TrailPerProductSubscriptionDetails } from './components/TrailPerProductSubscriptionDetails'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import { PlanTypes } from 'Types/PlansTypes'
import { useGetPlans } from 'Features/pricing/api/getPlans'
import { Plan } from 'Features/pricing/types/Plan'
import { SwitchPaymentIntervalModal } from './SwitchPaymentIntervalModal'
import { ProductTypes } from 'Types/ProductTypes'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { use12InstallmentsAllowChanges } from 'Hooks/split/use12InstallmentsChanges'
import { useLocalizedDate } from 'Hooks/useLocalizedDate'
import { addYears } from 'date-fns'
import { PaymentStatusTag } from './components/PaymentStatusTag'

export const CurrentPlanDetails = () => {
  const { t } = useTranslation('accountSettings')
  const { data: details, isFetching } = useGetPlansDetails()
  const isProductTrial = useIsProductTrial()
  const { isInstallmentsSubscription } = useUserStore()
  const isTrialSubscription = useIsTrialSubscription()
  const [showAnnuallyModal, setShowAnnuallyModal] = useState<boolean>(false)
  const [updatedPlans, setNewPlans] = useState<Plan[]>([])
  const { findPlan } = useGetPlan()
  const plans = useGetPlans()
  const { showToaster } = useAppStore()
  const allowInstallmentChanges = use12InstallmentsAllowChanges()
  const { origin } = useUserStore()
  const { getLocalizedDate } = useLocalizedDate()

  const hasActiveAndCanceledProducts = useMemo(() => {
    if (!details?.productCancelled) return false
    let active = false,
      canceled = false
    for (const key in details.productCancelled)
      details.productCancelled[key] ? (canceled = true) : (active = true)
    return active && canceled
  }, [details?.productCancelled])

  const isEarlyAdopter = details?.billingSubscriptionId === 'early-adopter'
  const hasData = !!(details && details?.plans && details.plans.length)

  const billingPeriodText = () => {
    if (hasData) {
      const { currentPeriodStart, currentPeriodEnd, trial, cancelled } = details
      const { endTime } = trial

      if (isTrialSubscription)
        return t(`plan_details.trial_${cancelled ? 'cancelled' : 'ends_on'}`, {
          trailEnd: getLocalizedDate(new Date(endTime)),
        })
      else {
        const parsedDate = new Date(currentPeriodStart)
        const installmentsEndDate = addYears(parsedDate, 1)
        return `${getLocalizedDate(new Date(currentPeriodStart))} - ${getLocalizedDate(
          isInstallmentsSubscription ? installmentsEndDate : new Date(currentPeriodEnd)
        )}`
      }
    }
  }

  const numberOfMonthSinceStart = details?.installmentMonth ? details.installmentMonth : 0

  const finalMonthlyAmountWithDiscount = details?.productsAmountWithDiscount
    ? details.productsAmountWithDiscount
    : 0

  const totalAmountWithFee =
    details?.oneTimeFee && finalMonthlyAmountWithDiscount
      ? `${getAmountWithCurrency(
          details?.plans[0]?.currency.toLocaleLowerCase(),
          Math.round(finalMonthlyAmountWithDiscount + details.oneTimeFee)
        )}`
      : ''

  const monthlyPayment = hasData
    ? `${getAmountWithCurrency(
        details?.plans[0]?.currency.toLocaleLowerCase(),
        Math.round(finalMonthlyAmountWithDiscount || 0)
      )}`
    : ''
  const oneTimeFeeNumber = details?.oneTimeFee ? Math.round(details.oneTimeFee) : 0

  const oneTimeFeeString =
    totalAmountWithFee && monthlyPayment && hasData
      ? `${getAmountWithCurrency(
          details?.plans[0]?.currency.toLocaleLowerCase(),
          oneTimeFeeNumber
        )}`
      : ''

  const annualPrice = useMemo(() => {
    if (!details || !details?.plans?.length) return ''
    const { productsInTrial } = isProductTrial
    const { productsAmountWithDiscount, totalAmount, plans, netAmountToPay } = details
    const currency = (plans[0] || {})?.currency.toLocaleLowerCase()
    const amount = getAmountWithCurrency(
      currency,
      Math.round(
        productsInTrial
          ? totalAmount
          : netAmountToPay || productsAmountWithDiscount || totalAmount || 0
      )
    )

    if (productsInTrial && !hasActiveAndCanceledProducts) return amount
    return `${amount}/ ${t('plan_details.annually')}`

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, isProductTrial.productsInTrial])

  const currentMonthForPriceInstallmentPlan =
    totalAmountWithFee && finalMonthlyAmountWithDiscount && hasData
      ? `${getAmountWithCurrency(
          details?.plans[0]?.currency.toLocaleLowerCase(),
          Math.round(finalMonthlyAmountWithDiscount || 0) * 12 + oneTimeFeeNumber
        )}`
      : ''

  const annualPriceForInstallmentPlanWithoutFee =
    totalAmountWithFee && finalMonthlyAmountWithDiscount && hasData
      ? `${getAmountWithCurrency(
          details?.plans[0]?.currency.toLocaleLowerCase(),
          Math.round(finalMonthlyAmountWithDiscount || 0) * 12
        )}`
      : ''

  const nextBillingText = details
    ? `${t('plan_details.next_billing')} ${getLocalizedDate(new Date(details.currentPeriodEnd))}`
    : ''

  const trailChargeText = details?.cancelled
    ? t('plan_details.access_available_until', {
        date: getLocalizedDate(new Date(details.trial.endTime)),
      })
    : details && isInstallmentsSubscription
    ? `${t('plan_details.next_payment_due')} ${getLocalizedDate(new Date(details.trial.endTime))}`
    : `${t('plan_details.will_be_charged')} `

  const newPlans: Plan[] = []
  const showModal = () => {
    if (plans.data?.length) {
      const allPlans = plans.data
      const paymentInterval = isInstallmentsSubscription ? 12 : 1
      const planA = details?.plans[0]
      const newPlanA = findPlan(
        allPlans,
        planA?.type as PlanTypes,
        planA?.currency || 'USD',
        planA?.product as ProductTypes,
        (planA?.limit ?? 0) / 1000,
        paymentInterval
      )
      if (newPlanA) {
        newPlans.push(newPlanA)
      }
      if (details?.plans[1]) {
        const planB = details?.plans[1]
        const newPlanB = findPlan(
          allPlans,
          planB.type as PlanTypes,
          planB.currency || 'USD',
          planB.product,
          planB?.limit / 1000,
          paymentInterval
        )
        if (newPlanB) {
          newPlans.push(newPlanB)
        }
      }
      if (details?.plans.length !== newPlans.length) {
        showToaster(t('plan_details.switch_payment_interval_modal.error_has_occurred'), 'failure')
      } else {
        setNewPlans(newPlans)
        setShowAnnuallyModal(true)
      }
    }
  }

  const showSwitchPaymentIntervalForInstallmentsUsers =
    allowInstallmentChanges && origin === 'Direct12Installments' && isTrialSubscription

  const tooltip = (
    <Tooltip
      tooltiptContent={<>{t('plan_details.tooltip_installments_payment_description')}</>}
      trigerStyle={{
        style: {
          color: theme.colors.gray.value,
          marginLeft: '4px',
          position: 'relative',
          top: '3px',
        },
        width: '18px',
        height: '18px',
      }}
    />
  )

  const showNextBillingDetails = details
    ? !details.cancelled || (details.cancelled && !details?.trial.active)
    : ''

  return (
    <>
      {isFetching && (
        <LoaderContainer>
          <LoadingIcon />
        </LoaderContainer>
      )}
      {!isEarlyAdopter && hasData && (
        <CurrentPlanWrapper>
          <FlexRow>
            <CurrentPlanTitle>{t('plan_details.current_plan')}</CurrentPlanTitle>
          </FlexRow>
          <CurrentPlanDetailsWrapper>
            <PlanList>
              {details.plans.map((item) => (
                <PlanListItem key={item.product}>
                  <PlanNameItem>
                    {t(`plan_details.${item.product}`)} - {item.type}
                  </PlanNameItem>
                  {!!item.limit && (
                    <PlanClicksItem>
                      {kFormatter(item.limit)} {t('plan_details.annually_protected_visits')}
                    </PlanClicksItem>
                  )}
                  {!!item.domains && (
                    <PlanClicksItem>
                      {kFormatter(item.domains)} {t('plan_details.allowed_domains')}
                    </PlanClicksItem>
                  )}
                </PlanListItem>
              ))}
            </PlanList>
            <InlineFlex>
              <PlanNameItem>{t(`plan_details.subscription_details`)}</PlanNameItem>
              <PaymentStatusTag />
            </InlineFlex>
            <CurrentPlanDetailsRow>{billingPeriodText()}</CurrentPlanDetailsRow>
            {isProductTrial.productsInTrial && (
              <TrailPerProductSubscriptionDetails
                endDate={getLocalizedDate(new Date(details?.trial.endTime))}
                productName={
                  isProductTrial.productsInTrial
                    ? getProductName(isProductTrial.productsInTrial)
                    : ''
                }
              />
            )}
            {showNextBillingDetails && (!details?.trial.active || !isProductTrial.allowed) && (
              <CurrentPlanDetailsRow>{nextBillingText}</CurrentPlanDetailsRow>
            )}
          </CurrentPlanDetailsWrapper>
          {showNextBillingDetails && (
            <CurrentPlanPrice>
              {isInstallmentsSubscription ? (
                details?.trial.active && numberOfMonthSinceStart === 1 ? (
                  <>
                    {totalAmountWithFee} /{' '}
                    {t('plan_details.first_installment', { monthlyPayment, oneTimeFeeString })}
                    {tooltip}
                    <CurrentPlanPrice>
                      {currentMonthForPriceInstallmentPlan} {t('plan_details.annually')}
                    </CurrentPlanPrice>
                  </>
                ) : (
                  <>
                    <CurrentPlanPrice>
                      {monthlyPayment} / {numberOfMonthSinceStart}{' '}
                      {t('plan_details.12_installments')}
                    </CurrentPlanPrice>
                    {!isTrialSubscription && (
                      <CurrentPlanPrice>
                        {t('plan_details.annually_without_fee', {
                          yearlyPrice: annualPriceForInstallmentPlanWithoutFee,
                          oneTimeFee: oneTimeFeeString,
                        })}
                        {tooltip}
                      </CurrentPlanPrice>
                    )}
                  </>
                )
              ) : (
                <>
                  {details?.trial.active && !hasActiveAndCanceledProducts && trailChargeText}
                  {details && <PlanPriceBold>{annualPrice}</PlanPriceBold>}
                </>
              )}
            </CurrentPlanPrice>
          )}

          {showSwitchPaymentIntervalForInstallmentsUsers && !details.cancelled && (
            <>
              <SwitchPaymentInterval onClick={() => showModal()}>
                {isInstallmentsSubscription
                  ? t('plan_details.switch_to_annual')
                  : t('plan_details.switch_to_monthly')}
              </SwitchPaymentInterval>
              <SwitchPaymentIntervalModal
                isOpen={showAnnuallyModal}
                onOpenChange={() => setShowAnnuallyModal(false)}
                plans={updatedPlans}
                isInstallmentsSubscription={isInstallmentsSubscription}
              />
            </>
          )}
        </CurrentPlanWrapper>
      )}
    </>
  )
}
